import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Button } from 'react-bootstrap';
import AppContext from 'context/Context';
import OptionListSelect from 'components/dropdowns/OptionListSelect';
import { targetTypeKey } from 'helpers/kpiHelper';
import {
  resolveValueKind,
  SIC_COMPARE_TYPE,
  SIC_COMPARE_TYPES,
  SIC_VALUE_KIND
} from 'helpers/sicHelper';
import { post, patch } from 'services/bs-sector-measurable/measurableService';
import {
  swalToastError,
  swalToastSuccess
} from 'components/utilities/SwalToastUtil';

const MANUAL_ENTER_INPUT_METHOD = 2;

const VALUE_KIND_HINTS = {
  [SIC_VALUE_KIND.date]:
    'Enter the completion date each day. Target is the number of working days allowed (Mon-Fri), e.g. 2 means due within +2 working days.',
  [SIC_VALUE_KIND.time]:
    'Enter a time (hh:mm) each day. Target is a time, e.g. 22:00.'
};

const DEFAULT_COMPARE_TYPE_BY_VALUE_KIND = {
  [SIC_VALUE_KIND.yesNo]: SIC_COMPARE_TYPE.equal,
  [SIC_VALUE_KIND.date]: SIC_COMPARE_TYPE.lessOrEqual,
  [SIC_VALUE_KIND.time]: SIC_COMPARE_TYPE.lessOrEqual
};

const DEFAULT_UOM_BY_VALUE_KIND = {
  [SIC_VALUE_KIND.date]: 'date',
  [SIC_VALUE_KIND.time]: 'hh:mm'
};

const isDefaultUom = uom =>
  Object.values(DEFAULT_UOM_BY_VALUE_KIND).includes(uom.trim());

const uomForSelectedValueKind = (previousUom, selectedValueKind) => {
  if (selectedValueKind === SIC_VALUE_KIND.yesNo) return '';
  const keepsUserUom = previousUom.trim() !== '' && !isDefaultUom(previousUom);
  if (keepsUserUom) return previousUom;
  return DEFAULT_UOM_BY_VALUE_KIND[selectedValueKind] ?? '';
};

const buildInitialFormData = (measurable, nextOrderNo) => ({
  name: measurable.name ?? '',
  orderNo: measurable.orderNo ?? nextOrderNo,
  targetType: measurable.targetType ?? null,
  uom:
    measurable.uom?.trim() ||
    DEFAULT_UOM_BY_VALUE_KIND[measurable.valueKind] ||
    '',
  numOfDecimal: measurable.numOfDecimal ?? 0,
  compareType: measurable.compareType ?? SIC_COMPARE_TYPE.greaterThan,
  affectsSic: measurable.affectsSic ?? true,
  variationPercentage: measurable.variationPercentage ?? ''
});

export default function SicMeasurableModal({
  measurable,
  sectorId,
  nextOrderNo,
  targetTypeIds,
  handleClose,
  handleSaved
}) {
  const {
    config: { isDark }
  } = useContext(AppContext);
  const isEditing = Boolean(measurable.id);
  const [formData, setFormData] = useState(
    buildInitialFormData(measurable, nextOrderNo)
  );
  const [isSaving, setIsSaving] = useState(false);
  const valueKind = resolveValueKind(formData.targetType, targetTypeIds);
  const isYesNoTarget = valueKind === SIC_VALUE_KIND.yesNo;
  const isNumberTarget = valueKind === SIC_VALUE_KIND.number;

  const updateField = (field, value) =>
    setFormData(previous => ({ ...previous, [field]: value }));

  const handleTargetTypeChange = option => {
    const selectedValueKind = resolveValueKind(option.id, targetTypeIds);
    const isSelectedNumber = selectedValueKind === SIC_VALUE_KIND.number;
    setFormData(previous => ({
      ...previous,
      targetType: option.id,
      compareType:
        DEFAULT_COMPARE_TYPE_BY_VALUE_KIND[selectedValueKind] ??
        previous.compareType,
      numOfDecimal: isSelectedNumber ? previous.numOfDecimal : 0,
      uom: uomForSelectedValueKind(previous.uom, selectedValueKind)
    }));
  };

  const buildRequest = () => ({
    name: formData.name.trim(),
    orderNo: Number(formData.orderNo) || 0,
    targetType: formData.targetType,
    uom: isYesNoTarget ? '' : formData.uom.trim(),
    numOfDecimal: Number(formData.numOfDecimal) || 0,
    compareType: formData.compareType,
    affectsSic: formData.affectsSic,
    variationPercentage:
      !isNumberTarget || formData.variationPercentage === ''
        ? null
        : Number(formData.variationPercentage)
  });

  const handleSubmit = async event => {
    event.preventDefault();
    if (!formData.targetType) {
      swalToastError('Please select a target type.');
      return;
    }

    setIsSaving(true);
    const response = isEditing
      ? await patch(measurable.id, buildRequest())
      : await post({
          ...buildRequest(),
          sectorId,
          inputMethod: MANUAL_ENTER_INPUT_METHOD,
          formula: null
        });
    setIsSaving(false);

    if (!response?.success) {
      swalToastError(response.message || 'Failed to save measurable.');
      return;
    }
    swalToastSuccess(isEditing ? 'Measurable updated' : 'Measurable created');
    handleSaved();
  };

  return (
    <Modal show onHide={handleClose} contentClassName="border">
      <Form onSubmit={handleSubmit}>
        <Modal.Header
          closeButton
          closeVariant={isDark ? 'white' : undefined}
          className="bg-light px-card border-bottom-0"
        >
          <Modal.Title as="h5">
            {isEditing ? 'Edit' : 'Create'} Measurable
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-card">
          <Form.Group className="mb-3">
            <Form.Label className="fs-0">Name</Form.Label>
            <Form.Control
              required
              value={formData.name}
              onChange={e => updateField('name', e.target.value)}
            />
          </Form.Group>

          <div className="d-flex mb-3" style={{ gap: '12px' }}>
            <Form.Group style={{ flex: 2 }}>
              <Form.Label className="fs-0">Target Type</Form.Label>
              <OptionListSelect
                optionKey={targetTypeKey}
                selected={formData.targetType ?? 0}
                changeFn={handleTargetTypeChange}
              />
            </Form.Group>
            {!isYesNoTarget && (
              <Form.Group style={{ flex: 1 }}>
                <Form.Label className="fs-0">UOM</Form.Label>
                <Form.Control
                  placeholder="qty, hrs, %..."
                  maxLength={20}
                  value={formData.uom}
                  onChange={e => updateField('uom', e.target.value)}
                />
              </Form.Group>
            )}
            <Form.Group style={{ flex: 1 }}>
              <Form.Label className="fs-0">Order No.</Form.Label>
              <Form.Control
                type="number"
                value={formData.orderNo}
                onChange={e => updateField('orderNo', e.target.value)}
              />
            </Form.Group>
            {isNumberTarget && (
              <Form.Group style={{ flex: 1 }}>
                <Form.Label className="fs-0">Decimals</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={formData.numOfDecimal}
                  onChange={e => updateField('numOfDecimal', e.target.value)}
                />
              </Form.Group>
            )}
          </div>

          {isYesNoTarget && (
            <div className="mb-3 fs--1 text-muted">
              Target is achieved when the daily answer is{' '}
              <strong>Yes</strong>.
            </div>
          )}

          {VALUE_KIND_HINTS[valueKind] && (
            <div className="mb-3 fs--1 text-muted">
              {VALUE_KIND_HINTS[valueKind]}
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="sic-affects-score"
              label="Affects SIC Score"
              checked={formData.affectsSic}
              onChange={e => updateField('affectsSic', e.target.checked)}
            />
          </Form.Group>

          {!isYesNoTarget && (
            <Form.Group className="mb-3">
              <Form.Label className="fs-0">
                Value compare with Target{' '}
                <span className="text-muted">(optional)</span>
              </Form.Label>
              <div className="d-flex flex-wrap mb-2" style={{ gap: '14px' }}>
                <span className="fs--1" style={{ paddingTop: '2px' }}>
                  Target achieved when value is:
                </span>
                {SIC_COMPARE_TYPES.map(option => (
                  <Form.Check
                    key={option.value}
                    type="radio"
                    id={`sic-compare-${option.value}`}
                    label={option.label}
                    checked={formData.compareType === option.value}
                    onChange={() => updateField('compareType', option.value)}
                  />
                ))}
              </div>
              {isNumberTarget && (
                <div
                  className="d-flex align-items-center"
                  style={{ gap: '10px' }}
                >
                  <span className="fs--1">Warning band (%)</span>
                  <Form.Control
                    type="number"
                    placeholder="Leave empty for pass / fail only"
                    value={formData.variationPercentage}
                    onChange={e =>
                      updateField('variationPercentage', e.target.value)
                    }
                    style={{ maxWidth: '260px' }}
                  />
                </div>
              )}
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light px-card border-top-0">
          <Button
            variant="primary"
            type="submit"
            className="px-4 mx-0"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

SicMeasurableModal.propTypes = {
  measurable: PropTypes.object.isRequired,
  sectorId: PropTypes.number.isRequired,
  nextOrderNo: PropTypes.number,
  targetTypeIds: PropTypes.shape({
    yesNo: PropTypes.number,
    date: PropTypes.number,
    time: PropTypes.number
  }).isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSaved: PropTypes.func.isRequired
};
